import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Terminal, Filter, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: '', label: 'All' },
  { id: 'cooldown', label: 'Cooldown' },
  { id: 'daily_limit', label: 'Daily limit' },
  { id: 'master_off', label: 'Master off' },
  { id: 'out_of_hours', label: 'Out of hours' },
  { id: 'human_handoff', label: 'Handoff' },
  { id: 'api_failure', label: 'API failure' },
  { id: 'error', label: 'Errors' },
];

const SEVERITY_STYLE = {
  error: { icon: AlertCircle, color: 'text-red-700 bg-red-50 border-red-200' },
  warn: { icon: AlertTriangle, color: 'text-amber-700 bg-amber-50 border-amber-200' },
  info: { icon: Info, color: 'text-blue-700 bg-blue-50 border-blue-200' },
};

const LogsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (severity) params.set('severity', severity);
      const res = await fetch(`${API_URL}/api/automation/chatbots/logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setItems(data.data || []);
    } catch (e) {
      console.error('logs load failed', e);
    } finally {
      setLoading(false);
    }
  }, [category, severity]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-4" data-testid="logs-tab">
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <Filter size={16} className="text-gray-500" />
        {CATEGORIES.map((c) => (
          <button
            key={c.id || 'all'}
            onClick={() => setCategory(c.id)}
            data-testid={`logs-filter-${c.id || 'all'}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              category === c.id
                ? 'bg-teal-600 text-white border-teal-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}
          >
            {c.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
            data-testid="logs-severity-select"
          >
            <option value="">All severities</option>
            <option value="info">Info</option>
            <option value="warn">Warn</option>
            <option value="error">Error</option>
          </select>
          <button
            onClick={load}
            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
            data-testid="logs-refresh-btn"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="animate-spin text-teal-600" size={28} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Terminal size={26} className="text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium">No logs yet</p>
            <p className="text-gray-400 text-sm mt-1">
              Engine events (cooldowns, failures, limits) will appear here.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((it) => {
              const sev = SEVERITY_STYLE[it.severity] || SEVERITY_STYLE.info;
              const Icon = sev.icon;
              return (
                <li
                  key={it.id}
                  className="p-4 flex items-start gap-3 hover:bg-gray-50"
                  data-testid={`log-item-${it.id}`}
                >
                  <span className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-lg border ${sev.color}`}>
                    <Icon size={14} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{new Date(it.createdAt).toLocaleString()}</span>
                      <span className="px-2 py-0.5 bg-gray-100 rounded uppercase tracking-wide">{it.category}</span>
                      {it.phone && <span className="font-mono">{it.phone}</span>}
                    </div>
                    <p className="mt-1 text-sm text-gray-800">{it.message}</p>
                    {it.extra && Object.keys(it.extra).length > 0 && (
                      <pre className="mt-1 text-xs text-gray-500 bg-gray-50 rounded p-2 overflow-x-auto">
                        {JSON.stringify(it.extra, null, 2)}
                      </pre>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default LogsTab;
